import { useContext, useMemo } from "react";
import useSpesificCustoms from "@/features/customField/api/useSpesificCustoms";
import { child_spesific_key } from "@/config/endpoints";
import { getCfDisplayName } from "@/features/customField/utils/cfDisplayName";
import {
  RequiredFieldMarksContext,
  type RequiredFieldMark,
  type RequiredFieldMarkIndex,
} from "@/features/leads/requirements/requiredFieldMarks";

/**
 * Add Application → Preferences: EVERY visible selector must be filled before
 * the deal is created (owner 2026-09-21: "make these fields mandatory, the
 * visible fields"). It used to be only the RED-starred ones (blocking stage
 * gates), so a deal still went in with no Intake Year / Month / Type of
 * Interview whenever the tenant had not gated them. A selector with NO options
 * is skipped — there is nothing the user could pick, so it must never block.
 *
 * `index` is the field's POSITION in the SELECTOR `/specifics` list — the same
 * `customInputScopes.<index>` slot `PrefrenceFormRenderer` binds, so an error
 * set there lands on the right picker.
 */
export type RequiredApplicationPref = {
  customInputId: number;
  index: number;
  label: string;
};

/** Tooltip on the star of a field that is required only by this form. */
export const APPLICATION_PREF_REQUIRED_REASON =
  "Required to create an application";

export function requiredApplicationPrefs(
  fields: any[] | undefined,
): RequiredApplicationPref[] {
  if (!fields?.length) return [];
  const out: RequiredApplicationPref[] = [];
  fields.forEach((field, index) => {
    if (field?.type !== "SELECTOR") return;
    if (!Array.isArray(field.options) || field.options.length === 0) return;
    const id = Number(field.id);
    out.push({
      customInputId: id,
      index,
      label: getCfDisplayName(field) || "a required field",
    });
  });
  return out;
}

/** The required prefs the submitted scopes leave empty (matched by field id). */
export function missingApplicationPrefs(
  required: RequiredApplicationPref[],
  scopes: unknown,
): RequiredApplicationPref[] {
  const list = Array.isArray(scopes) ? scopes : [];
  return required.filter((r) => {
    const scope = list.find(
      (s: any) => Number(s?.customInputId) === r.customInputId,
    );
    const picked = Array.isArray(scope?.optionIds)
      ? scope.optionIds.filter((v: unknown) => v != null && String(v).trim() !== "")
      : [];
    return picked.length === 0;
  });
}

/**
 * The marks index with a blocking star laid over every required pref, so the
 * pickers inside `ChildPrefrenceCard` show the red star. A field that already
 * carries a blocking stage-gate mark keeps its own ("Required to reach …").
 */
export function useApplicationPrefMarks(
  required: RequiredApplicationPref[],
): RequiredFieldMarkIndex {
  const base = useContext(RequiredFieldMarksContext);
  return useMemo(() => {
    const custom = new Map<number, RequiredFieldMark>(base?.custom ?? []);
    for (const r of required) {
      if (custom.get(r.customInputId)?.blocking === true) continue;
      custom.set(r.customInputId, {
        blocking: true,
        where: "",
        reason: APPLICATION_PREF_REQUIRED_REASON,
      });
    }
    return {
      fields: base?.fields ?? new Map(),
      modules: base?.modules ?? new Map(),
      custom,
    } as RequiredFieldMarkIndex;
  }, [base, required]);
}

/** Same field query as `ChildPrefrenceCard`'s renderer (cache hit). */
export function useRequiredApplicationPrefs(): RequiredApplicationPref[] {
  const { spesificFields } = useSpesificCustoms({
    queryKey: child_spesific_key,
    modelName: "child",
    type: "SELECTOR",
  });
  return useMemo(
    () => requiredApplicationPrefs(spesificFields),
    [spesificFields],
  );
}
